"use client";

import * as React from "react";
import { Dialog as PrimeDialog } from "primereact/dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  dismissableMask?: boolean;
  closable?: boolean;
}

const Dialog = ({ open, onOpenChange, children, dismissableMask = false, closable = false }: DialogProps) => {
  return (
    <PrimeDialog
      visible={open ?? false}
      onHide={() => onOpenChange?.(false)}
      modal
      dismissableMask={dismissableMask}
      className="p-dialog-custom"
      showHeader={false}
      closable={closable}
      style={{ background: "var(--p-surface-card, var(--p-surface-0, #fff))", borderRadius: "1rem" }}
      contentStyle={{ padding: 0, background: "var(--p-surface-card, var(--p-surface-0, #fff))", borderRadius: "1rem" }}
    >
      {children}
    </PrimeDialog>
  );
};

const DialogTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
>(({ className, children, asChild, ...props }, ref) => {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, { ...props, ref } as any);
  }
  return (
    <button ref={ref} className={cn("p-button", className)} {...props}>
      {children}
    </button>
  );
});
DialogTrigger.displayName = "DialogTrigger";

const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, style, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col flex-1 overflow-y-auto rounded-2xl shadow-xl bg-white dark:bg-gray-900",
      className
    )}
    style={{ background: "var(--p-surface-card, var(--p-surface-0, #fff))", ...style }}
    {...props}
  >
    {children}
  </div>
));
DialogContent.displayName = "DialogContent";

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("px-4 sm:px-6 pb-4 sm:pb-6 pt-3 sm:pt-4 border-t flex flex-col-reverse sm:flex-row gap-2", className)} style={{ borderColor: "var(--p-border-color)" }} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3 ref={ref} className={cn("text-lg font-bold", className)} {...props} />
));
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm opacity-70", className)} {...props} />
));
DialogDescription.displayName = "DialogDescription";

const DialogClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      "absolute right-4 top-4 h-10 w-10 rounded-2xl transition-all flex items-center justify-center",
      className
    )}
    style={{ color: "var(--p-text-muted-color)" }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = "var(--p-surface-100)";
      e.currentTarget.style.color = "var(--p-text-color)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = "transparent";
      e.currentTarget.style.color = "var(--p-text-muted-color)";
    }}
    {...props}
  >
    <X className="h-5 w-5" />
  </button>
));
DialogClose.displayName = "DialogClose";

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
